export async function apiFetch(endpoint, options = {}) {
  let res;
  try {
    res = await fetch(`/api${endpoint}`, {
      ...options,
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });
  } catch (error) {
    throw { status: 0, error: 'Network request failed', network: true, cause: error };
  }

  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw { status: res.status, ...data };
  return data;
}
export const authApi = {
  login: (key) => apiFetch('/auth/login', { method: 'POST', body: JSON.stringify({ key }) }),
  me: () => apiFetch('/auth/me'),
  logout: () => apiFetch('/auth/logout', { method: 'POST' }),
  completeHandover: () => apiFetch('/auth/complete-handover', { method: 'POST' }),
  saveBio: (bio) => apiFetch('/auth/bio', { method: 'POST', body: JSON.stringify(bio) }),
  getBio: () => apiFetch('/auth/bio'),
  saveGoals: (goals) => apiFetch('/auth/goals', { method: 'POST', body: JSON.stringify({ goals }) }),
  approveGuide: () => apiFetch('/auth/approve-guide', { method: 'POST' }),
  introComplete: () => apiFetch('/auth/intro-complete', { method: 'POST' }),
  deleteData: () =>
    apiFetch('/auth/delete-data', {
      method: 'POST',
      body: JSON.stringify({ confirmation: 'DELETE' }),
    }),
  registerPrivate: (data) => apiFetch('/auth/register/private', { method: 'POST', body: JSON.stringify(data) }),
  registerOrg: (data) => apiFetch('/auth/register/org', { method: 'POST', body: JSON.stringify(data) }),
  lookupDemoCode: (code: string) => apiFetch(`/auth/demo-code/${encodeURIComponent(code)}`),
  registerCode: (data: {
    code: string;
    email: string;
    name: string;
    organisationName?: string;
    serviceAgreementAccepted?: boolean;
  }) => apiFetch('/auth/register/code', { method: 'POST', body: JSON.stringify(data) }),
  enterBriefingRoom: () => apiFetch('/auth/enter-briefing-room', { method: 'POST' }),
  orgDashboard: () => apiFetch('/auth/org/dashboard'),
  saveOrgCloudStorage: (cloudStorage: Record<string, unknown>) =>
    apiFetch('/auth/org/cloud-storage', { method: 'POST', body: JSON.stringify(cloudStorage) }),
  guardianFeed: () => apiFetch('/auth/org/guardian-feed'),
  enrollMentee: (data: Record<string, string | number | undefined>) =>
    apiFetch('/auth/org/enroll-mentee', { method: 'POST', body: JSON.stringify(data) }),
  generateKey: () => apiFetch('/auth/org/generate-key', { method: 'POST' }),
  createMentor: (data: {
    name: string;
    phone?: string;
    email?: string;
    backgroundBrief?: string;
  }) => apiFetch('/auth/org/mentors', { method: 'POST', body: JSON.stringify(data) }),
  listMentors: () => apiFetch('/auth/org/mentors'),
  listArchives: () => apiFetch('/auth/org/archives'),
  assignMenteeToMentor: (mentorId: number, menteeToken: string) =>
    apiFetch(`/auth/org/mentors/${mentorId}/assign`, {
      method: 'POST',
      body: JSON.stringify({ menteeToken }),
    }),
  archiveMentor: (mentorId: number) =>
    apiFetch(`/auth/org/mentors/${mentorId}/archive`, { method: 'POST' }),
  restoreMentor: (mentorId: number) =>
    apiFetch(`/auth/org/mentors/${mentorId}/restore`, { method: 'POST' }),
  archiveMentee: (menteeToken: string) =>
    apiFetch(`/auth/org/mentees/${encodeURIComponent(menteeToken)}/archive`, { method: 'POST' }),
  restoreMentee: (menteeToken: string) =>
    apiFetch(`/auth/org/mentees/${encodeURIComponent(menteeToken)}/restore`, { method: 'POST' }),
  verifyMentorKey: (activationKey: string) =>
    apiFetch('/auth/mentor/verify-key', { method: 'POST', body: JSON.stringify({ activationKey }) }),
  getAssignedMentor: () => apiFetch('/auth/assigned-mentor'),
};

export const cloudApi = {
  status: (scope: 'user' | 'org' = 'user') =>
    apiFetch(`/cloud/status?scope=${scope}`),
  startGoogleOAuth: (params: {
    scope?: 'user' | 'org';
    returnTo?: string;
    folderLabel?: string;
  } = {}) => {
    const q = new URLSearchParams();
    q.set('scope', params.scope || 'user');
    if (params.returnTo) q.set('returnTo', params.returnTo);
    if (params.folderLabel) q.set('folderLabel', params.folderLabel);
    return apiFetch(`/cloud/google/start?${q.toString()}`);
  },
  disconnectGoogle: (scope: 'user' | 'org' = 'user') =>
    apiFetch('/cloud/google/disconnect', {
      method: 'POST',
      body: JSON.stringify({ scope }),
    }),
  listFolders: (scope: 'user' | 'org' = 'user', q = '') =>
    apiFetch(`/cloud/google/folders?scope=${scope}&q=${encodeURIComponent(q)}`),
  saveFolder: (payload: {
    scope?: 'user' | 'org';
    folder_id: string;
    folder_label?: string;
  }) =>
    apiFetch('/cloud/google/folder', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  listFiles: (scope: 'user' | 'org' = 'user') =>
    apiFetch(`/cloud/google/files?scope=${scope}`),
  saveSelectedFiles: (payload: {
    scope?: 'user' | 'org';
    files: Array<Record<string, unknown>>;
  }) =>
    apiFetch('/cloud/selected-files', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  getSelectedFiles: (scope: 'user' | 'org' = 'user') =>
    apiFetch(`/cloud/selected-files?scope=${scope}`),
  verifyGoogle: (scope: 'user' | 'org' = 'user') =>
    apiFetch('/cloud/google/verify', {
      method: 'POST',
      body: JSON.stringify({ scope }),
    }),
};

export const mentorConsoleApi = {
  login: (activationKey: string) =>
    apiFetch('/mentor-console/login', { method: 'POST', body: JSON.stringify({ activationKey }) }),
  me: () => apiFetch('/mentor-console/me'),
  logout: () => apiFetch('/mentor-console/logout', { method: 'POST' }),
  dashboard: () => apiFetch('/mentor-console/dashboard'),
  crisisDetail: (crisisEventId: number) => apiFetch(`/mentor-console/crisis/${crisisEventId}`),
  saveNotes: (data: {
    menteeToken: string;
    notesText?: string;
    followUpAgenda?: string;
    nextMeetingAt?: string;
  }) => apiFetch('/mentor-console/notes', { method: 'POST', body: JSON.stringify(data) }),
  deEscalate: (crisisEventId: number) =>
    apiFetch('/mentor-console/de-escalate', { method: 'POST', body: JSON.stringify({ crisisEventId }) }),
  confirmCalendar: (crisisEventId: number) =>
    apiFetch('/mentor-console/calendar/confirm', { method: 'POST', body: JSON.stringify({ crisisEventId }) }),
  rescheduleCalendar: (crisisEventId: number, newStartIso: string) =>
    apiFetch('/mentor-console/calendar/reschedule', {
      method: 'POST',
      body: JSON.stringify({ crisisEventId, newStartIso }),
    }),
  removeMentee: (menteeToken: string) =>
    apiFetch(`/mentor-console/mentees/${encodeURIComponent(menteeToken)}/remove`, { method: 'POST' }),
};

export const chatApi = {
  sendMessage: (content) => apiFetch('/chat/message', { method: 'POST', body: JSON.stringify({ content }) }),
  getHistory: () => apiFetch('/chat/history'),
  consoleState: () => apiFetch('/chat/console-state'),
  synthesisStatus: () => apiFetch('/chat/synthesis-status'),
  submitReflection: (cycleNumber, reflection) =>
    apiFetch('/chat/synthesis/reflect', { method: 'POST', body: JSON.stringify({ cycleNumber, reflection }) }),
};

export const guestChatApi = {
  sendMessage: (data: {
    content: string;
    guestSessionId: string;
    focusArea?: string | null;
    messages?: Array<{ role: string; content: string; focusArea?: string }>;
  }) => apiFetch('/guest-chat/message', { method: 'POST', body: JSON.stringify(data) }),
  getVoiceId: () => apiFetch('/guest-chat/voice-id'),
};

export const supportApi = {
  submitTicket: (data: {
    name: string;
    email: string;
    phone: string;
    description: string;
    attachments?: Array<{ filename: string; mimeType: string; size: number; data?: string }>;
  }) => apiFetch('/support/ticket', { method: 'POST', body: JSON.stringify(data) }),
};

export async function getStripeKey() {
  const res = await fetch('/api/config/stripe-key');
  return res.json();
}

export const procurementApi = {
  generateInvoice: (data: {
    administratorName: string;
    organisationName: string;
    corporateEmail: string;
    contactPhone: string;
    businessAddress: string;
    poNumber?: string;
    accountsPayableEmail?: string;
    planKey: string;
    seatCount?: number;
  }) => apiFetch('/procurement/generate-invoice', { method: 'POST', body: JSON.stringify(data) }),

  downloadInvoicePdf: async (invoiceNumber: string) => {
    const response = await fetch(
      `/api/procurement/invoice/${encodeURIComponent(invoiceNumber)}/pdf`,
      { credentials: 'include' },
    );

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw { status: response.status, error: data.error || 'Unable to download invoice PDF.' };
    }

    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = `Mentology-Invoice-${invoiceNumber}.pdf`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(objectUrl);
  },
};

export const checkoutApi = {
  createEmbeddedSession: (data: {
    planKey: string;
    customerEmail: string;
    customerName?: string;
    companyName?: string;
    seatCount?: number;
    address?: string;
    phone?: string;
    successPath?: string;
  }) => apiFetch('/checkout/create-embedded-session', { method: 'POST', body: JSON.stringify(data) }),
  createSession: (data: {
    planKey: string;
    customerEmail: string;
    customerName?: string;
    companyName?: string;
    seatCount?: number;
    address?: string;
    phone?: string;
    successPath?: string;
    guestTranscriptJson?: string;
  }) => apiFetch('/checkout/create-session', { method: 'POST', body: JSON.stringify(data) }),
  getFulfillment: (sessionId: string) =>
    apiFetch(`/checkout/fulfillment/${encodeURIComponent(sessionId)}`),
};

export const masterAdminApi = {
  listPrivateMentees: () => apiFetch('/master-admin/private-mentees'),
  listOrganisations: () => apiFetch('/master-admin/organisations'),
  listDemoCodes: () => apiFetch('/master-admin/demo-codes'),
  getMailStatus: () => apiFetch('/master-admin/mail-status'),
  getDemoCodeEmailPreview: (code: string) =>
    apiFetch(`/master-admin/demo-codes/${encodeURIComponent(code)}/email-preview`),
  createDemoCode: (data: {
    track: 'PRO' | 'ELT';
    registeredEmail: string;
    registeredName?: string;
    ledgerRecord?: string[];
  }) => apiFetch('/master-admin/demo-codes', { method: 'POST', body: JSON.stringify(data) }),
  sendDemoCodeEmail: (code: string) =>
    apiFetch(`/master-admin/demo-codes/${encodeURIComponent(code)}/send-email`, { method: 'POST' }),
  deleteDemoCode: (code: string) =>
    apiFetch(`/master-admin/demo-codes/${encodeURIComponent(code)}`, { method: 'DELETE' }),
  deletePrivateMentee: (menteeKey: string) =>
    apiFetch(`/master-admin/private-mentees/${encodeURIComponent(menteeKey)}`, { method: 'DELETE' }),
  deleteOrganisation: (organisationKey: string) =>
    apiFetch(`/master-admin/organisations/${encodeURIComponent(organisationKey)}`, { method: 'DELETE' }),
};

export const voiceApi = {
  getRegistry: () => apiFetch('/voice/registry'),
  health: () => apiFetch('/voice/health'),
  preview: (() => {
    let chain: Promise<unknown> = Promise.resolve();

    return async (voiceId: string, text: string, modelId?: string, languageTarget?: string) => {
      // Serialize ElevenLabs preview calls to avoid concurrent_limit_exceeded delays.
      const run = chain.then(async () => {
        const res = await fetch('/api/voice/preview', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json', Accept: 'audio/mpeg' },
          body: JSON.stringify({
            voiceId,
            text,
            modelId,
            languageTarget,
          }),
        });

        const contentType = res.headers.get('content-type') || '';

        if (!res.ok || !contentType.includes('audio')) {
          const data = contentType.includes('json')
            ? await res.json().catch(() => ({}))
            : { error: await res.text().catch(() => 'Voice preview failed') };
          throw { status: res.status, ...data };
        }

        const blob = await res.blob();
        if (!blob.size) {
          throw { status: res.status, error: 'Empty audio stream' };
        }

        return blob;
      });

      chain = run.catch(() => undefined);
      return run;
    };
  })(),
};
